import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/admin`;

// "حالات الباند": banned drivers, drivers close to a ban, drivers with many user cancellations,
// and the drivers alerted about all of it.
export const useBans = () => {
    const user = useSelector((state) => state.userController.user);
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const request = useCallback(async (path, { method = 'GET', body } = {}) => {
        const response = await fetch(`${API}${path}`, {
            method,
            headers: {
                Authorization: `Bearer ${user?.token}`,
                ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            },
            ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json.error || 'حدث خطأ غير متوقع');
        return json;
    }, [user?.token]);

    const notify = useCallback((msg, type = 'success') => dispatch(alertActions.showAlert({ msg, type })), [dispatch]);

    const load = useCallback(async () => {
        if (!user?.token) return;
        setLoading(true);
        try {
            setData(await request('/bans'));
        } catch (error) {
            notify(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [user?.token, request, notify]);

    useEffect(() => { load(); }, [load]);

    // Each action reloads the lists; returns true on success.
    const run = async (promise, successMsg) => {
        try {
            const json = await promise;
            notify(successMsg || json.message || 'تم');
            await load();
            return true;
        } catch (error) {
            notify(error.message, 'error');
            return false;
        }
    };

    const banDriver = (driverId, { days, hours, reason }) =>
        run(request(`/bans/drivers/${driverId}/ban`, { method: 'POST', body: { days, hours, reason } }));
    const liftBan = (driverId) => run(request(`/bans/drivers/${driverId}/lift`, { method: 'POST' }));
    const removeLogEntry = (driverId, kind, entry) => {
        const qs = new URLSearchParams({ tripNumber: String(entry.tripNumber), date: new Date(entry.date).toISOString() });
        return run(request(`/bans/drivers/${driverId}/logs/${kind}?${qs}`, { method: 'DELETE' }), 'تم حذف السجل');
    };
    const saveAlertDrivers = (driverIds) =>
        run(request('/bans/alert-drivers', { method: 'PUT', body: { driverIds } }), 'تم حفظ قائمة الإشعارات');
    const searchDrivers = async (q) => {
        try {
            return (await request(`/bans/drivers/search?q=${encodeURIComponent(q)}`)).drivers || [];
        } catch (error) {
            notify(error.message, 'error');
            return [];
        }
    };

    return { data, loading, load, banDriver, liftBan, removeLogEntry, saveAlertDrivers, searchDrivers };
};
